<template>
	<div class="p4">
		<!-- Clause selection -->
		<core-form-input
			class="mb6"
			name="selectedClause"
			componentName="j-base-multi-select-with-flexible-value"
			:allowLocalSearch="true"
			:label="$t('select_clause')"
			:options="clauseOptions"
			v-model="selectedClause"
		></core-form-input>

		<!-- Order -->
		<div class="fs4 c1 mb6">
			<core-form-input
				componentName="j-base-input-number"
				name="clauseOrder"
				:label="$t('order')"
				v-model="clauseOrder"
			></core-form-input>
		</div>

		<!-- selection -->
		<div v-if="selectedClause" class="flex xs-middle xs-right mb6">
			<j-base-toggle size="xsmall" v-model="isOverride" />
			<div class="fw7 ml2">{{ $t("override") }}</div>
		</div>

		<!-- ContentJson -->
		<p
			v-if="!isOverride && contentJson && Object.keys(contentJson).length"
			class="xs-12 fs4 fw7 lh125 mt4 mb2"
		>
			{{ $t("dynamic_content") }}
		</p>
		<div
			v-show="!isOverride"
			v-for="(item, key) in contentJson"
			:key="key"
			class="xs-middle mb3"
		>
			<!-- Label -->
			<div class="xs-12 fs4 fw4 mb1">{{ key }}</div>
			<!-- value -->
			<div class="xsl-12 pr1">
				<core-form-input
					componentName="j-base-input-text"
					:name="`name-${key}`"
					size="small"
					:value="contentJson[key]"
					@input="handleInput(key, $event)"
				/>
			</div>
		</div>

		<!-- Override area -->
		<p v-if="isOverride" class="xs-12 fs4 fw7 lh125 mt4 mb2">HTML</p>
		<j-spinner v-if="isRendering" />
		<core-rich-text-editor
			v-if="isOverride && !isRendering"
			:content="htmlOverride"
			@input="updateHtml"
		/>
	</div>
</template>

<script>
import JButton from "@/components/JButton"
import JSpinner from "@/components/JSpinner"
import JBaseToggle from "@/components/JBaseToggle"
import CoreRichTextEditor from "@/modules/core/components/CoreRichTextEditor"
import JCard from "@/components/JCard"
import JExpendableBox from "@/components/JExpendableBox"
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import { Liquid } from "liquidjs"

import api from "@/api/gql.js"
import { gql } from "@/api/utils.js"

export default {
	components: {
		JButton,
		JCard,
		JExpendableBox,
		CoreFormInput,
		JSpinner,
		CoreRichTextEditor,
		JBaseToggle
	},
	props: {
		value: Object,
		newClausesLength: {
			type: Number,
			default: 0
		},
		tag: String
	},
	data() {
		return {
			type: "content",
			selectedClause: null,
			contentJson: null,
			variablesJson: null,
			clauseOptions: [],
			clauses: [],
			clauseOrder: null,
			isOverride: false,
			htmlOverride: "",
			isRendering: false
		}
	},
	watch: {
		// Render html when we toggle isOverride ; remove htmlOverride when we untoggle isOverride
		// That way, we don't override a clause if we go back to the dynamic content
		isOverride(newVal) {
			if (newVal) {
				this.renderClause()
			} else {
				this.$emit("input", {
					...this.value,
					htmlOverride: null
				})
			}
		},

		selectedClause(newVal) {
			if (newVal && newVal.length) {
				this.clauseOrder = newVal[0].order
				this.contentJson = this.parseHtmlToSchema(this.computedHtml)
				const v = this.value
				v.templateId = newVal[0].id
				v.renderParameters.name = "data"
				v.order = this.clauseOrder
				this.$emit("input", v)
			}
		},

		clauseOrder(newVal) {
			if (newVal && newVal.length) {
				this.$emit("input", { ...this.value, order: newVal })
			}
		},

		contentJson: {
			handler(val) {
				// value.renderParameters.contentJsonString
				this.$emit("input", {
					...this.value,
					renderParameters: {
						contentJsonString: JSON.stringify(val),
						name: "data",
						variablesJsonString: null
					}
				})
			},
			deep: true,
			immediate: true
		}
	},
	methods: {
		async fetchClauses() {
			const query = gql`
				query fetchClauseTemplates($where: templateWhereInput) {
					templates(where: $where) {
						list {
							id
							name
							... on clauseHtml {
								html
								order
							}
						}
					}
				}
			`
			const variables = {
				where: {
					and: [
						{ type: "clauseHtml" },
						{
							tags_contains: this.tag
						}
					]
				}
			}
			const res = await api({ query, variables })
			this.clauseOptions = res?.data?.templates?.list.map(item => ({
				id: item.id,
				name: item.name,
				order: item?.order
			}))
			this.clauses = res?.data?.templates?.list
		},

		parseHtmlToSchema(htmlString) {
			const liquidjs = new Liquid()

			return liquidjs
				.parse(htmlString)
				?.filter(item => item.hasOwnProperty("value"))
				?.map(item => ({
					key: item?.value?.initial?.replace("data.", ""),
					value: item?.value?.filters
						?.find(obj => obj.name === "default")
						?.args?.[0].replace(/'|"/g, "")
				}))
				?.reduce(
					(obj, item) => ({
						...obj,
						[item.key]: item.value
					}),
					{}
				)
		},

		handleInput(key, $event) {
			if (this.checkIsNum($event)) this.contentJson[key] = parseInt($event)
			else this.contentJson[key] = $event
		},

		checkIsNum(value) {
			return /^\d+$/.test(value)
		},

		async renderClause() {
			const query = gql`
				query renderClause(
					$html: String!
					$input: templateRenderParametersInput!
				) {
					renderHtml(html: $html, input: $input) {
						errors
						status
						value
					}
				}
			`
			const variables = {
				html: this.computedHtml,
				input: {
					name: "data",
					contentJsonString: JSON.stringify(this.contentJson)
				}
			}
			this.isRendering = true
			const res = await api({ query, variables })
			this.isRendering = false

			this.htmlOverride = res?.data?.renderHtml?.value
		},

		updateHtml(input) {
			this.htmlOverride = input
			this.$emit("input", {
				...this.value,
				htmlOverride: input
			})
		}
	},
	computed: {
		computedHtml() {
			if (this.selectedClause?.length > 0) {
				return this.clauses?.find(
					clause => clause.id === this.selectedClause?.[0]?.id
				)?.html
			} else return ""
		}
	},

	created() {
		this.fetchClauses()
	}
}
</script>

<style></style>
